import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, Switch } from '@appcues/sonar';
import { FieldSet, Label, Select } from 'ext/components/ui';
import { BlockContentShape } from 'entities/block/shape';
import { SINGLE_SELECT, MULTI_SELECT } from 'lib/block';
import { HelpLabel } from 'components/SideBarSettings/Shared';
import {
  Controls,
  GroupedFieldSet,
  GroupedField,
} from 'components/SideBarSettings/Shared/styled';
import useAccordionClick from 'components/SideBarSettings/Shared/use-accordion-click';
import RequiredField from 'components/SideBarSettings/Survey/RequiredField';

export default function SelectBehavior({
  blockType,
  options = [],
  minSelections,
  maxSelections,
  required,
  randomizeOptionOrder = false,
  onChange,
  onRequiredChange,
  onErrorLabelChange,
}) {
  const trackAccordion = useAccordionClick();

  const selectionOptions = Array.from({ length: options.length + 1 }).map(
    (_, index) => ({
      label: `${index}`,
      value: index,
    })
  );

  const nonMinimumOption = [selectionOptions.shift()];

  const handleToggleRandomizeOptionOrder = checked => {
    onChange({ randomizeOptionOrder: checked });
  };

  return (
    <Accordion.Item value="behavior">
      <Accordion.Header>
        <Accordion.Trigger onClick={() => trackAccordion('Select', 'Behavior')}>
          Behavior
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content>
        <Controls>
          {blockType === MULTI_SELECT && (
            <GroupedFieldSet>
              <GroupedField half>
                <Label htmlFor="min-selections" isDisabled={!required}>
                  Min selections
                </Label>
                <Select
                  id="min-selections"
                  aria-label="Min selections"
                  options={
                    required
                      ? selectionOptions.filter(({ value }) =>
                          maxSelections ? value <= maxSelections : true
                        )
                      : nonMinimumOption
                  }
                  isDisabled={!required}
                  value={
                    required
                      ? selectionOptions.filter(
                          ({ value }) => value === minSelections
                        )
                      : nonMinimumOption
                  }
                  onChange={({ value }) => onChange({ minSelections: value })}
                  portal
                />
              </GroupedField>
              <GroupedField half>
                <Label htmlFor="max-selections">Max selections</Label>
                <Select
                  id="max-selections"
                  aria-label="Max selections"
                  options={selectionOptions.filter(({ value }) =>
                    minSelections ? value >= minSelections : true
                  )}
                  placeholder="None"
                  value={selectionOptions.filter(
                    ({ value }) => value === maxSelections
                  )}
                  onChange={({ value }) => onChange({ maxSelections: value })}
                  portal
                />
              </GroupedField>
            </GroupedFieldSet>
          )}
          <RequiredField
            onRequiredChange={onRequiredChange}
            onErrorLabelChange={onErrorLabelChange}
          />
          <FieldSet>
            <Switch
              id="randomize-option-order"
              checked={randomizeOptionOrder}
              onCheckedChange={handleToggleRandomizeOptionOrder}
              fullWidth
            >
              <Switch.Label htmlFor="randomize-option-order">
                Randomize option order
                <HelpLabel>
                  Randomly shuffle the order of the
                  <br />
                  options each time the flow is shown.
                  <br />
                  Requires SDK versions ≥ 5.0.0
                </HelpLabel>
              </Switch.Label>
            </Switch>
          </FieldSet>
        </Controls>
      </Accordion.Content>
    </Accordion.Item>
  );
}

SelectBehavior.propTypes = {
  blockType: PropTypes.oneOf([SINGLE_SELECT, MULTI_SELECT]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      content: BlockContentShape,
      value: PropTypes.string,
    })
  ),
  minSelections: PropTypes.number,
  maxSelections: PropTypes.number,
  required: PropTypes.bool,
  randomizeOptionOrder: PropTypes.bool,
  onChange: PropTypes.func,
  onRequiredChange: PropTypes.func,
  onErrorLabelChange: PropTypes.func,
};
